import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AssessmentWrapper from "./assessmentWrapper";
import ProctoringWrapper from "../../components/molecules/proctoringWrapper";
import { useShowErrors } from "./_components/toasterWrapper";

import {
  useLazyGetAssessmentOngoingPreQuery,
  useLazyHeartBeatApiQuery,
  useLazyGetCommonConfigDataQuery
} from "../../store/slices/api/assessmentApiSlice";
import { useAppDispatch, useAppSelector } from "../../store/store";
import AssessmentSlice, {
  AssessmentSliceActions
} from "../../store/slices/assessmentSlice";
import GetHeroIcon from "../../utils/getHeroIcons";
import { PreAssessmentData } from "../../types/preAssessment";
import { AppMetaDataSliceActions } from "@lxp-app/store/slices/appMetaDataSlice";
import { DarkmodeAction } from "@lxp-app/store/slices/darkmodeSlice";
import { useSelector } from "react-redux";
import { RootState } from "@lxp-app/store/store";
import ErrorListAlerts from "@lxp-app/pages/event-creation-workflow/errorListAlert";
import { useKeycloak } from "@react-keycloak/web";
import { useLazySendLoginActivityQuery } from "@lxp-app/store/slices/api/userInfoApiSlice";
import { UserInfoSliceActions } from "@lxp-app/store/slices/userInfoSlice";
import LanguageSwitcher from "@assessment/components/molecules/languageSwitcher";

const AssessmentPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const showErrors = useShowErrors();

  const { scheduleId } = useParams();

  const { keycloak } = useKeycloak();

  const [online, setOnline] = useState(false);
  const [offline, setOffline] = useState(false);

  const constants = useAppSelector((store) => store.AssessmentSlice.duration);

  const [getAssessmentOngoingPre, { isFetching, isLoading, isError }] =
    useLazyGetAssessmentOngoingPreQuery();
  const [heartBeatApi] = useLazyHeartBeatApiQuery();
  const [getConstants] = useLazyGetCommonConfigDataQuery();
  const ongoingIdRef = useRef("");

  const preAssessmentData = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData
  );

  const [sendLoginActivity] = useLazySendLoginActivityQuery();

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);

  const mode = useSelector((state: RootState) => state.DarkmodeSlice);

  const userProfile = useSelector(
    (state: RootState) => state?.UserInfoSlice?.profile
  );
  const hasAssessmentRecOrgs = userProfile?.hasAssessmentRecOrgs;

  const savedId = useAppSelector((state) => state.AssessmentSlice.visitorId);

  const tabId = sessionStorage.getItem("assessmentTabId");
  let visitorId = localStorage.getItem("assessmentVisitorId");
  const hasBroadcasted = useRef(false);

  const starterCode = useAppSelector(
    (store) => store.AssessmentSlice.starterCodeList
  );

  const coding = useAppSelector(
    (store) =>
      store.AssessmentSlice.preAssessmentData?.questionTypeCountMap?.coding
  );

  useLayoutEffect(() => {
    if (AppMetaDataSliceActions) {
      dispatch(AppMetaDataSliceActions.setViewBreadcrumb(false));
    }

    return () => {
      if (AppMetaDataSliceActions) {
        dispatch(AppMetaDataSliceActions.setViewBreadcrumb(true));
      }
    };
  }, []);

  useEffect(() => {
    if (ongoingId != null) {
      ongoingIdRef.current = ongoingId;
    }
  }, [ongoingId]);

  useEffect(() => {
    const channel = new BroadcastChannel("app-channel");
    const runtimeId = crypto.randomUUID();

    channel.onmessage = (event) => {
      const data = event.data;

      if (
        data.visitorId === visitorId &&
        data.tabId === tabId &&
        data.runtimeId !== runtimeId
      ) {
        navigate("/");
      }
    };

    if (visitorId && tabId) {
      channel.postMessage({
        type: "SESSION_TAB_READY",
        visitorId,
        tabId,
        runtimeId
      });
    }

    return () => channel.close();
  }, [visitorId, tabId]);

  useEffect(() => {
    if (visitorId && tabId) {
      let existingId = `${visitorId}-${tabId}`;
      console.log(savedId, existingId);
      if (
        savedId &&
        existingId &&
        savedId != null &&
        existingId != null &&
        savedId != "" &&
        existingId != ""
      ) {
        if (savedId != existingId) {
          const arr = savedId.split("-");
          if (arr[0] != visitorId) {
            if (keycloak.authenticated) {
              sendLoginActivity("logout").then((res: any) => {
                dispatch(UserInfoSliceActions.setFirstTimeLogin(false));
                keycloak.logout({
                  redirectUri:
                    window.location.origin +
                    "/logout" +
                    `?org=${userProfile?.isHitachiOrg ? "hitachi" : userProfile?.isTMobileOrg ? "t-mobile" : ""}`
                });
              });
              sessionStorage.clear();
            }
          } else navigate("/");
        } else console.log("Same ID", savedId, existingId);
      }
    }
  }, [savedId, visitorId, tabId]);

  useEffect(() => {
    let intervalId: any;

    intervalId = setInterval(() => {
      heartBeatApiCall();
    }, constants?.heartBeatInterval * 1000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);
  useEffect(() => {
    if (coding > 0 && Object.keys(starterCode)?.length == 0) {
      (async () => {
        const res: any = await getConstants("starterCodeBE");
        if (res?.data?.data?.value?.starterCode) {
          dispatch(
            AssessmentSliceActions.mutateStarterCodeList(
              res?.data?.data?.value?.starterCode
            )
          );
        }
      })();
    }
  }, [coding]);

  useEffect(() => {
    (async () => {
      try {
        if (!scheduleId) {
          throw {
            status: "",
            title: t("assessment.scheduleIdNotFound"),
            err: {},
            description: ""
          };
        }

        const raw = await getAssessmentOngoingPre({
          scheduleId: scheduleId
        });

        if (raw.error)
          throw {
            status: raw.status,
            title: t("assessment.page.errorFetchingPreAssessmentData"),
            err: raw.error,
            description: ""
          };

        const data: PreAssessmentData = raw?.data?.data;
        dispatch(AssessmentSliceActions.mutatePreAssessmentData(data));
      } catch (err) {
        console.error(err);
        const temp: any = err;
        const rawErrList = temp?.err?.data?.error;
        const errList =
          rawErrList?.length > 0
            ? rawErrList?.map((el: string) => el.split("_")[0])
            : [];
        if (showErrors)
          showErrors(temp?.title, errList, "error", temp?.description);
      }
    })();
    return () => {
      dispatch(AssessmentSliceActions.mutateAssessmentHasStarted(false));
    };
  }, [scheduleId]);

  useEffect(() => {
    (async () => {
      const res: any = await getConstants("assessmentConsts");
      if (res?.data?.data?.value) {
        dispatch(
          AssessmentSliceActions.mutateConstants(res?.data?.data?.value)
        );
      }
    })();
    function goOnline() {
      dispatch(AssessmentSliceActions.mutateOnline(true));
      setOffline(false);
      setOnline(true);
      heartBeatApiCall();
    }
    function goOffline() {
      dispatch(AssessmentSliceActions.mutateOnline(false));
      setOnline(false);
      setOffline(true);
    }

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const heartBeatApiCall = async () => {
    try {
      if (!scheduleId) {
        throw {
          status: "",
          title: t("assessment.scheduleIdNotFound"),
          err: {},
          description: ""
        };
      }
      if (!ongoingIdRef.current) {
        throw {
          status: "",
          title: t("assessment.ongoingIdNotFound"),
          err: {},
          description: ""
        };
      }
      const heartBeatRes = await heartBeatApi({
        ongoingId: ongoingIdRef.current,
        scheduleId: scheduleId
      });
      if (heartBeatRes?.data?.data?.sessionId) {
        dispatch(
          AssessmentSliceActions.mutateVisitorId(
            heartBeatRes?.data?.data?.sessionId
          )
        );
      }
      if (heartBeatRes?.data?.data?.ongoingQPDuration) {
        dispatch(
          AssessmentSliceActions.mutateTimeLeft(
            heartBeatRes?.data?.data?.ongoingQPDuration
          )
        );
      }
    } catch (error: any) {
      console.log("error", error);
    }
  };

  if (isFetching || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-screen gap-5">
        <div className="w-8 h-8 border-t rounded-full border-t-primary border-secondary animate-spin" />
        <span>{t("common.loading")}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-screen gap-5">
        <GetHeroIcon
          name="ExclamationTriangleIcon"
          className="w-6 h-6"
          variant="outline"
        />
        <span>{t("assessment.fetchError", { featureName: "assessment" })}</span>
      </div>
    );
  }

  if (!preAssessmentData) {
    return;
  }

  const videoProctoring = preAssessmentData.videoProctoring;
  const browserProctoring = preAssessmentData.browserProctoring;

  return (
    <>
      <div>
        <ProctoringWrapper videoProctoring={videoProctoring}>
          <AssessmentWrapper
            browserProctoring={browserProctoring}
            videoProctoring={videoProctoring}
          />
        </ProctoringWrapper>
      </div>

      {online && (
        <ErrorListAlerts
          severity="success"
          label={t("assessment.page.connectionRestoredLabel")}
          description={t("assessment.page.connectionRestoredDescription")}
          messageList={[]}
          autoHideDuration={5000}
        />
      )}
      {offline && (
        <ErrorListAlerts
          severity="error"
          label={t("assessment.page.offlineLabel")}
          description={t("assessment.page.offlineDescription")}
          messageList={[]}
        />
      )}
    </>
  );
};

export default AssessmentPage;
